import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { readCookieConsent, writeCookieConsent } from '@/lib/cookieConsent';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const lastActiveElementRef = useRef<HTMLElement | null>(null);
  const previousOverflowRef = useRef<string>('');

  useEffect(() => {
    const consent = readCookieConsent();
    if (consent === 'unset') {
      // Small delay before showing the banner
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    lastActiveElementRef.current = document.activeElement as HTMLElement | null;
    previousOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusId = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });
    return () => {
      window.cancelAnimationFrame(focusId);
      document.body.style.overflow = previousOverflowRef.current;
      lastActiveElementRef.current?.focus();
    };
  }, [isVisible]);

  const handleAccept = () => {
    writeCookieConsent('accepted');
    setIsVisible(false);
    console.log('Cookies accepted');
  };

  const handleDecline = () => {
    writeCookieConsent('declined');
    setIsVisible(false);
    console.log('Cookies declined');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleDecline();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
    );

    if (!focusable || focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const activeElement = document.activeElement as HTMLElement | null;

    if (event.shiftKey && activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
            onClick={handleDecline}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', bounce: 0.3, duration: 0.5 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          >
            <div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="cookie-banner-title"
              aria-describedby="cookie-banner-description"
              className="bg-card rounded-2xl shadow-2xl border border-border p-6 md:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onKeyDown={handleKeyDown}
            >
              {/* Close button */}
              <button
                ref={closeButtonRef}
                onClick={handleDecline}
                type="button"
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Schließen"
              >
                <X className="w-5 h-5" aria-hidden="true" />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Cookie className="w-8 h-8 text-primary" aria-hidden="true" />
                </div>
              </div>

              {/* Content */}
              <div className="text-center mb-6">
                <h3 id="cookie-banner-title" className="text-xl font-display font-semibold text-card-foreground mb-3">
                  Cookie-Einstellungen
                </h3>
                <p id="cookie-banner-description" className="text-muted-foreground text-sm leading-relaxed">
                  Wir verwenden Cookies, um Ihnen das beste Erlebnis auf unserer Webseite zu bieten. 
                  Einige Cookies sind für die Funktion der Seite notwendig, während andere uns helfen, 
                  die Nutzererfahrung zu verbessern.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleDecline}
                  type="button"
                  className="flex-1 px-6 py-3 rounded-xl border border-border text-muted-foreground font-medium hover:bg-muted transition-colors"
                >
                  Nur notwendige
                </button>
                <button
                  onClick={handleAccept}
                  type="button"
                  className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-primary to-glow-secondary text-primary-foreground font-semibold hover-glow shadow-lg"
                >
                  Alle akzeptieren
                </button>
              </div>

              {/* Privacy Link */}
              <p className="text-center mt-4">
                <Link 
                  to="/datenschutz" 
                  className="text-foreground text-sm underline hover:text-primary transition-colors"
                >
                  Mehr erfahren
                </Link>
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
